import { useState, useEffect } from "react";
import { Button, ToggleSwitch, Spinner, TextInput, Select, Label } from "flowbite-react";
import PaginatedSelect from "@/components/PaginatedSelect";
import { HiPencil, HiTrash, HiBan, HiFilter } from "react-icons/hi";
import { MdOutlineSettingsBackupRestore } from "react-icons/md";
import { Category } from "@/sharedTypes";
import DataTable from "@/components/Table";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isCompanyAdminUser } from "@/constants/agent";
import {
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useRestoreCategoryMutation,
  useHardDeleteCategoryMutation,
} from "@/api/ticketApi/category";
import { useGetClientGroupsQuery } from "@/api/clientGroupApi/clientGroup";
import { useGetPrioritiesQuery } from "@/api/ticketApi/selectInputApis";
import { toast } from "@/lib/toast";
import { getErrorMessage } from "@/utils/requestUtils";
import CategoryFormDialog from "./CategoryFormDialog";
import ConfirmationDialog from "@/components/ConfirmationDialog";

interface CategoryListTableProps {
  data: any[]; // Table expects array
  pagination: any;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  filters: any;
  onFilterChange: (filters: any) => void;
}

export default function CategoryListTable({
  data,
  pagination,
  onPageChange,
  isLoading,
  filters,
  onFilterChange,
}: CategoryListTableProps) {
  const { t } = useTranslation(["common", "category"]);
  const user = useCurrentUser();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<
    Category | undefined
  >();
  const [showInactive, setShowInactive] = useState(false);
  const [categoryNameFilter, setCategoryNameFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [groupFilter, setGroupFilter] = useState<{ label: string; value: string } | null>(null);
  const [debouncedCategoryName, setDebouncedCategoryName] = useState("");

  const [groupPage, setGroupPage] = useState(1);
  const [groupSearch, setGroupSearch] = useState("");

  const [showFilters, setShowFilters] = useState(false);

  const { data: prioritiesData } = useGetPrioritiesQuery();
  const { data: groupsData, isFetching: isGroupsFetching } = useGetClientGroupsQuery({ 
    pagination: { page: groupPage, pageSize: 15 },
    filters: { group_name: groupSearch }
  });

  const groupOptions = (groupsData?.items || []).map((g: any) => ({
    label: g.group_name,
    value: String(g.client_group_id)
  }));

  const [updateCategory] = useUpdateCategoryMutation();
  const [deleteCategory] = useDeleteCategoryMutation();
  const [restoreCategory] = useRestoreCategoryMutation();
  const [hardDeleteCategory] = useHardDeleteCategoryMutation();

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  } | null>(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedCategoryName(categoryNameFilter);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [categoryNameFilter]);

  useEffect(() => {
    const activeFilters: Record<string, any> = { ...filters, is_active: !showInactive };

    if (debouncedCategoryName) {
      activeFilters["category_name[like]"] = debouncedCategoryName;
    } else {
      delete activeFilters["category_name[like]"];
    }

    if (priorityFilter) {
      activeFilters.priority_id = parseInt(priorityFilter);
    } else {
      delete activeFilters.priority_id;
    }

    if (groupFilter) {
      activeFilters.group_id = parseInt(groupFilter.value);
    } else {
      delete activeFilters.group_id;
    }
    
    // Cleanup group search params if filter is cleared
     if (!groupFilter && groupSearch) {
        setGroupSearch("");
     }

    onFilterChange(activeFilters);
  }, [showInactive, debouncedCategoryName, priorityFilter, groupFilter]);

  const handleClearFilters = () => {
    setCategoryNameFilter("");
    setPriorityFilter("");
    setGroupFilter(null);
    setGroupSearch("");
    setShowInactive(false);
  };

  const handleToggleFilters = () => {
    if (showFilters) {
      handleClearFilters();
    }
    setShowFilters(!showFilters);
  };

  const handleEdit = (category: Category) => {
    setSelectedCategory({
      ...category,
      group_ids: category.groups?.map((g) => g.group_id) || [],
    });
    setIsEditModalOpen(true);
  };

  const handleUpdate = (updatedData: Category) => {
    if (!selectedCategory) return;
    const payload = {
      ...updatedData,
      category_id: selectedCategory.category_id,
    };
    updateCategory(payload)
      .unwrap()
      .then(() => {
        toast.success(t("category:categoryUpdated"));
        setIsEditModalOpen(false);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handleDelete = (id: number) => {
    setConfirmDialog({
      open: true,
      title: t("category:disableCategory"),
      description: t("category:confirmDisable"),
      onConfirm: () => {
        deleteCategory(id)
          .unwrap()
          .then(() => {
            toast.success(t("category:categoryDisabled"));
            setConfirmDialog(null);
          })
          .catch((err) => toast.error(getErrorMessage(err)));
      },
    });
  };

  const handleRestore = (id: number) => {
    setConfirmDialog({
      open: true,
      title: t("category:restoreCategory"),
      description: t("category:confirmRestore"),
      onConfirm: () => {
        restoreCategory(id)
          .unwrap()
          .then(() => {
            toast.success(t("category:categoryRestored"));
            setConfirmDialog(null);
          })
          .catch((err) => toast.error(getErrorMessage(err)));
      },
    });
  };

  const handleHardDelete = (id: number) => {
    setConfirmDialog({
      open: true,
      title: t("category:permanentlyDelete"),
      description: t("category:confirmPermanentDelete"),
      onConfirm: () => {
        hardDeleteCategory(id)
          .unwrap()
          .then(() => {
            toast.success(t("category:categoryPermanentlyDeleted"));
            setConfirmDialog(null);
          })
          .catch((err) => toast.error(getErrorMessage(err)));
      },
    });
  };

  const columns = [
    { key: "category_name", label: t("category:categoryName") },
    { key: "category_description", label: t("category:categoryDescription") },
    {
      key: "groups",
      label: t("category:group"),
      render: (row: Category) => {
        if (!row.groups || row.groups.length === 0) return <span>—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {row.groups.map((g) => (
              <span
                key={g.group_id}
                className={`inline-block rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-300 ${!g.is_active ? "italic opacity-50" : ""}`}
              >
                {g.group_name}
              </span>
            ))}
          </div>
        );
      },
    },
    ...(isCompanyAdminUser(user)
      ? [
          {
            key: "actions",
            label: t("category:actions"),
            render: (row: Category) => (
              <div className="flex gap-2">
                {!showInactive ? (
                  <>
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => handleEdit(row)}
                    >
                      <HiPencil className="h-4 w-4 text-purple-600" />
                    </Button>
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => handleDelete(row.category_id)}
                    >
                      <HiBan className="h-4 w-4 text-orange-600" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => handleRestore(row.category_id)}
                      title={t("category:restoreCategory")}
                    >
                      <MdOutlineSettingsBackupRestore className="h-4 w-4 text-green-600" />
                    </Button>
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => handleHardDelete(row.category_id)}
                      title={t("category:permanentlyDelete")}
                    >
                      <HiTrash className="h-4 w-4 text-red-600" />
                    </Button>
                  </>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <div className="mb-6 flex justify-start">
        <Button color="light" onClick={handleToggleFilters}>
          <HiFilter className="mr-2 h-5 w-5" />
          {showFilters ? t("common:hideFilters") : t("common:showFilters")}
        </Button>
      </div>

      {showFilters && (
        <div className="mb-6 rounded-lg bg-white p-4 shadow dark:bg-gray-800">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            {t("common:filters")}
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div>
              <Label htmlFor="category-search" className="mb-2 block">
                {t("category:categoryName")}
              </Label>
              <TextInput
                id="category-search"
                placeholder={t("category:searchCategory")}
                className="mt-4"
                value={categoryNameFilter}
                onChange={(e) => setCategoryNameFilter(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="priority-filter" className="mb-2 block">
                {t("category:priority")}
              </Label>
              <Select
                id="priority-filter"
                value={priorityFilter}
                className="mt-4"
                onChange={(e) => setPriorityFilter(e.target.value)}
              >
                <option value="">{t("category:allPriorities")}</option>
                {prioritiesData?.items?.map((p: any) => (
                  <option key={p.priority_id} value={p.priority_id}>
                    {p.priority_name}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <Label htmlFor="group-filter" className="mb-2 block">
                {t("category:group")}
              </Label>
              <PaginatedSelect
                id="group-filter"
                value={groupFilter}
                onChange={(val) => setGroupFilter(val as { label: string; value: string } | null)}
                options={groupOptions}
                onInputChange={(val) => { setGroupSearch(val); setGroupPage(1); }}
                isLoading={isGroupsFetching}
                placeholder={t("category:allGroups")}
                page={groupPage}
                totalPages={groupsData?.pagination?.totalPages || 1}
                onPageChange={setGroupPage}
                isClearable
                menuMaxHeight={350}
              />
            </div>

            <div className="flex flex-col justify-end gap-4 pb-2">
              <ToggleSwitch
                checked={showInactive}
                label={t("category:showInactive")}
                onChange={setShowInactive}
              />
              <Button color="light" size="sm" onClick={handleClearFilters}>
                {t("common:reset")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center p-10">
          <Spinner size="xl" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={data}
          pagination={pagination}
          onPageChange={onPageChange}
        />
      )}

      {isEditModalOpen && (
        <CategoryFormDialog
          mode="edit"
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={handleUpdate}
          defaultValues={selectedCategory}
          hasInactiveGroups={selectedCategory?.groups?.some((g) => g.is_active === false)}
        />
      )}

      {confirmDialog && (
        <ConfirmationDialog
          title={confirmDialog.title}
          body={confirmDialog.description}
          onCancel={() => setConfirmDialog(null)}
          onConfirm={confirmDialog.onConfirm}
        />
      )}
    </div>
  );
}
